import React, { useEffect, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Form, Row, Col, Card, Upload, Button, message } from 'antd';
import { uploadDocument } from '@app/api/prImportFreeSales.api';

interface DocumentChecklistProps {
  editable: boolean;
  initialformFields: DocumentType[][] | DocumentType[];
  formFields: {
    name: string;
    document_id: string;
  }[];
  status: string | null;
  pr_id: string | null;
  productListData: any;
  prLocationData?: any;
  product_type: string;
}

interface DocumentType {
  name: string;
  document_id: string;
  status: string;
  url?: string;
  catalog_product_id?: string;
  pr_location_id?: string;
  file?: string;
}

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  initialformFields: propInitialFields,
  formFields,
  status,
  pr_id,
  productListData,
  prLocationData,
  product_type,
}) => {
  const [uploading, setUploading] = useState<Record<string, boolean>>({});

  // Extract product data based on category
  const getProductData = () => {
    if (!productListData?.catalog) return { products: [], defaultProductId: '' };

    if (product_type === 'MULTIPLE') {
      return {
        products: productListData.catalog || [],
        defaultProductId: productListData.catalog.length > 0 ? productListData.catalog[0].product_id : '',
      };
    } else {
      return {
        products: [productListData.catalog],
        defaultProductId: productListData.catalog.product_id || '',
      };
    }
  };

  const {
    products,
    defaultProductId,
  }: { products: Array<{ product_id: string; product_name: string }>; defaultProductId: string } = getProductData();

  const normalizedLocation = Array.isArray(prLocationData) ? prLocationData[0] : prLocationData;

  const getEntityId = (): string => {
    if (product_type === 'LOCATION' && normalizedLocation?.id) {
      return normalizedLocation.id.toString();
    }
    return defaultProductId;
  };

  const [documentFields, setDocumentFields] = useState<Array<DocumentType>>(() => {
    if (Array.isArray(propInitialFields)) {
      const fields = Array.isArray(propInitialFields[0])
        ? (propInitialFields as DocumentType[][]).flat()
        : (propInitialFields as DocumentType[]);

      console.log('Initial document fields:', fields);
      return fields;
    }
    return [];
  });

  const [fileList, setFileList] = useState<Record<string, any>>({});

  useEffect(() => {
    if (Array.isArray(propInitialFields)) {
      const fields = Array.isArray(propInitialFields[0])
        ? (propInitialFields as DocumentType[][]).flat()
        : (propInitialFields as DocumentType[]);

      setDocumentFields(fields);
    }
  }, [propInitialFields]);

  // Initialize file list from document fields
  useEffect(() => {
    const initialFiles: Record<string, any> = {};

    documentFields.forEach((field) => {
      if (field.url) {
        const productId = getEntityId();
        const fileKey = `${field.document_id}_${productId}`;

        initialFiles[fileKey] = [
          {
            uid: fileKey,
            name: field.file || field.name,
            status: 'done',
            url: field.url,
          },
        ];
      }
    });

    setFileList(initialFiles);
  }, [documentFields, defaultProductId]);

  const extractDocumentRequirements = () => {
    // If formFields is provided and not empty, use it
    if (formFields && formFields.length > 0) {
      return formFields;
    }

    // If we have document fields but no form fields, extract unique document types
    if (documentFields && documentFields.length > 0) {
      // Create a map to store unique document types
      const uniqueDocTypes = new Map<string, { name: string; document_id: string }>();

      documentFields.forEach((doc) => {
        if (doc.document_id && doc.name) {
          uniqueDocTypes.set(doc.document_id, {
            name: doc.name,
            document_id: doc.document_id,
          });
        }
      });

      const extractedFormFields = Array.from(uniqueDocTypes.values());

      return extractedFormFields;
    }

    console.log('No document requirements found');
    return [];
  };

  const documentRequirements = extractDocumentRequirements();

  const processUploadResponse = (response: any) => {
    if (!response?.data?.pr_documents) return;

    const prDocuments = response.data.pr_documents;
    const responseDocuments: DocumentType[] = [];

    // Process the nested structure of documents by product
    Object.keys(prDocuments).forEach((productId) => {
      const productDocuments = prDocuments[productId];
      if (Array.isArray(productDocuments)) {
        productDocuments.forEach((doc) => {
          responseDocuments.push({
            ...doc,
            status: 'PENDING_DOCUMENT',
            ...(product_type === 'LOCATION' ? { pr_location_id: getEntityId() } : { catalog_product_id: productId }),
          });
        });
      }
    });

    if (responseDocuments.length > 0) {
      // Create a map of existing documents
      const existingDocumentsMap = new Map<string, DocumentType>();

      documentFields.forEach((doc) => {
        const key = `${doc.document_id}_${doc.catalog_product_id || defaultProductId}`;
        existingDocumentsMap.set(key, doc);
      });

      responseDocuments.forEach((doc) => {
        const key = `${doc.document_id}_${doc.catalog_product_id || defaultProductId}`;
        existingDocumentsMap.set(key, doc);
      });

      const updatedDocumentFields = Array.from(existingDocumentsMap.values());

      setDocumentFields(updatedDocumentFields);

      const newFileList = { ...fileList };
      responseDocuments.forEach((doc) => {
        if (doc.url) {
          const productId = doc.catalog_product_id || defaultProductId;
          const fileKey = `${doc.document_id}_${productId}`;
          newFileList[fileKey] = [
            {
              uid: fileKey,
              name: doc.file || doc.name,
              status: 'done',
              url: doc.url,
            },
          ];
        }
      });
      setFileList(newFileList);
    }
  };

  const handleUpload = async (documentId: string, file: File, productId: string) => {
    if (!pr_id) {
      message.error('Product registration ID is required');
      return;
    }

    const uploadKey = `${documentId}_${productId}`;

    try {
      setUploading((prev) => ({ ...prev, [uploadKey]: true }));

      const formData = new FormData();
      formData.append('document_id', documentId);
      formData.append('file', file, file.name);
      if (product_type === 'LOCATION') {
        const locId = getEntityId();
        console.log('Uploading with pr_location_id:', locId, 'Raw prLocationData:', normalizedLocation);
        formData.append('pr_location_id', locId);
      } else {
        console.log('Uploading with catalog_product_id:', productId);
        formData.append('catalog_product_id', productId);
      }

      const response = await uploadDocument(Number(pr_id), formData);

      if (response?.data?.pr_documents) {
        processUploadResponse(response);
        message.success(`${file.name} uploaded successfully`);
      } else {
        message.error(`Failed to upload ${file.name}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error(`Failed to upload ${file.name}`);
    } finally {
      setUploading((prev) => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleChange = async (documentId: string, file: any, productId: string) => {
    const fileList = file.fileList.filter((file: any) => file.originFileObj);
    const fileKey = `${documentId}_${productId}`;

    if (fileList.length > 0) {
      const fileObj = fileList[0].originFileObj;

      setFileList((prev) => ({
        ...prev,
        [fileKey]: fileList,
      }));

      await handleUpload(documentId, fileObj, productId);
    } else {
      message.error('No file detected, please try again.');
    }
  };

  // Get document fields for a specific product
  const getDocumentFieldsForProduct = (productId: string) => {
    // Filter document fields for this specific product
    const productSpecificDocs = documentFields.filter((doc) => {
      return product_type === 'LOCATION' ? doc.pr_location_id === productId : doc.catalog_product_id === productId;
    });

    const productDocsMap: Record<string, DocumentType> = {};
    productSpecificDocs.forEach((doc) => {
      if (doc.document_id) {
        productDocsMap[doc.document_id] = doc;
      }
    });

    return documentRequirements.map((req) => {
      const existingDoc = productDocsMap[req.document_id];

      if (existingDoc) {
        return {
          ...req,
          ...existingDoc,
          status: existingDoc?.status || 'PENDING_DOCUMENT',
          ...(product_type === 'LOCATION' ? { pr_location_id: productId } : { catalog_product_id: productId }),
        };
      }

      return {
        ...req,
        status: 'PENDING_DOCUMENT',
        catalog_product_id: productId,
      };
    });
  };

  // Prepare document fields for non-category 6
  const getStandardDocumentFields = () => {
    if (documentRequirements.length === 0 && documentFields.length > 0) {
      return documentFields;
    }

    // Create a map of document fields for lookup
    const documentsMap: Record<string, DocumentType> = {};
    documentFields.forEach((field) => {
      if (field.document_id) {
        documentsMap[field.document_id] = field;
      }
    });

    return documentRequirements.map((field) => {
      const existingDoc = documentsMap[field.document_id];

      if (existingDoc) {
        return {
          ...field,
          ...existingDoc,
          status: existingDoc.status || 'PENDING_DOCUMENT',
          image_url: existingDoc.url || '',
        };
      } else {
        return {
          ...field,
          status: 'PENDING_DOCUMENT',
          image_url: '',
        };
      }
    });
  };

  const standardDocumentFields = getStandardDocumentFields();

  const renderDocumentFields = () => {
    if (product_type === 'MULTIPLE') {
      if (!products || products.length === 0) {
        return (
          <Col span={24}>
            <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-center">No products available.</div>
          </Col>
        );
      }

      const hasUploadedDocuments = documentFields.some((doc) => doc.url);

      return (
        <>
          {products.map((product) => {
            const productId = product.product_id;
            const productDocumentFields = getDocumentFieldsForProduct(productId);

            console.log(`Fields for product ${productId}:`, productDocumentFields);

            return (
              <div key={productId} className="mb-8">
                <h3>Checklist For {product.product_name}</h3>
                <br />
                <Row gutter={[16, 16]}>
                  {productDocumentFields && productDocumentFields.length > 0 ? (
                    productDocumentFields.map((field) => {
                      const fileKey = `${field.document_id}_${productId}`;
                      const isUploading = uploading[fileKey] || false;
                      const currentFiles = fileList[fileKey] || [];

                      const hasUrl =
                        field.url ||
                        documentFields.some(
                          (doc) =>
                            doc.document_id === field.document_id && doc.catalog_product_id === productId && doc.url,
                        );

                      return (
                        <Col xs={24} sm={12} key={fileKey}>
                          <Card className="h-full bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                            <Form.Item
                              label={<span className="text-gray-700 font-medium">{field.name}</span>}
                              name={fileKey}
                              className="mb-0"
                            >
                              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <Upload
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  beforeUpload={() => false}
                                  fileList={currentFiles}
                                  onChange={(file) => handleChange(field.document_id, file, productId)}
                                  showUploadList={{ showRemoveIcon: false }}
                                  className="w-full"
                                  disabled={status !== 'PENDING_DOCUMENT'}
                                >
                                  <Button size="small" disabled={isUploading || status !== 'PENDING_DOCUMENT'}>
                                    <UploadOutlined className="text-gray-500" />
                                    <span>
                                      {isUploading
                                        ? 'Uploading...'
                                        : currentFiles.length > 0 || hasUrl
                                        ? 'Re-Upload'
                                        : 'Upload'}
                                    </span>
                                  </Button>
                                </Upload>
                                {(currentFiles.length > 0 || hasUrl) && (
                                  <div className="mt-2 text-green-500 text-sm">✓ File uploaded</div>
                                )}
                              </div>
                            </Form.Item>
                          </Card>
                        </Col>
                      );
                    })
                  ) : (
                    <Col span={24}>
                      <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-center">
                        No document fields for this product.
                      </div>
                    </Col>
                  )}
                </Row>
                <br />
              </div>
            );
          })}
        </>
      );
    } else {
      const productId = getEntityId();

      return (
        <Row gutter={[16, 16]}>
          {standardDocumentFields.length > 0 ? (
            standardDocumentFields.map((field) => {
              const fileKey = `${field.document_id}_${productId}`;
              const isUploading = uploading[fileKey] || false;
              const currentFiles = fileList[fileKey] || [];

              return (
                <Col xs={24} sm={12} key={fileKey}>
                  <Card className="h-full bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                    <Form.Item
                      label={<span className="text-gray-700 font-medium">{field.name}</span>}
                      name={fileKey}
                      className="mb-0"
                    >
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <Upload
                          accept=".pdf,.jpg,.jpeg,.png"
                          beforeUpload={() => false}
                          fileList={currentFiles}
                          onChange={(file) => handleChange(field.document_id, file, productId)}
                          showUploadList={{ showRemoveIcon: false }}
                          className="w-full"
                          disabled={status !== 'PENDING_DOCUMENT'}
                        >
                          <Button size="small" disabled={isUploading || status !== 'PENDING_DOCUMENT'}>
                            <UploadOutlined className="text-gray-500" />
                            <span>
                              {isUploading ? 'Uploading...' : currentFiles.length > 0 ? 'Re-Upload' : 'Upload'}
                            </span>
                          </Button>
                        </Upload>
                        {currentFiles.length > 0 && <div className="mt-2 text-green-500 text-sm">✓ File uploaded</div>}
                      </div>
                    </Form.Item>
                  </Card>
                </Col>
              );
            })
          ) : (
            <Col span={24}>
              <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-center">No documents available.</div>
            </Col>
          )}
        </Row>
      );
    }
  };

  return (
    <div className="p-6 bg-gray-50 rounded-lg">
      <Form layout="vertical">{renderDocumentFields()}</Form>
    </div>
  );
};

export default DocumentChecklist;
